import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import {
  IConfiguredAlertData,
  IConfiguredAlertItem,
  ISubscriptionItem
} from "../@types";
import api from "../../../services/api";
import { configuredAlertsKeys } from "../queryKeys";
import {
  useAlertSubscriptionDefaults,
  useAlertTypes,
  useSubscriptionHashes,
  useYaraSubscription
} from "./index";
import { ALERT_TYPES_MAP } from "../const";

const getInitialFormValues = (
  shouldUseYaraSubscription: boolean,
  shouldUseHashes: boolean,
  defaultSubscriptions: ISubscriptionItem[],
  hashValueSupportedAlertTypes: string[]
): IConfiguredAlertData => {
  const starterAlertValues: IConfiguredAlertData = {
    id: null,
    name: "",
    description: "",
    subscriptions: [],
    actions: []
  };

  if (!shouldUseHashes && !shouldUseYaraSubscription) {
    return starterAlertValues;
  }

  if (shouldUseYaraSubscription) {
    const yaraSubscriptionDefaults = defaultSubscriptions.filter(
      sub => sub.type === ALERT_TYPES_MAP.YARA_MATCH
    );
    return {
      ...starterAlertValues,
      subscriptions: yaraSubscriptionDefaults
    };
  }

  const subscriptionsWithHashValues = defaultSubscriptions.filter(defaultSub =>
    hashValueSupportedAlertTypes.includes(defaultSub.type)
  );

  return {
    ...starterAlertValues,
    subscriptions: subscriptionsWithHashValues
  };
};

const useConfiguredAlertFormValues = (alertId?: string) => {
  const { subscriptionHashes, shouldUseHashes } = useSubscriptionHashes();
  const { getHashValueSupportedAlertTypes } = useAlertTypes();
  const { yaraSubscriptions, shouldUseYaraSubscription } =
    useYaraSubscription();
  const { defaultSubscriptions } = useAlertSubscriptionDefaults({
    shouldUseHashes,
    subscriptionHashes,
    shouldUseYaraSubscription,
    yaraSubscriptions
  });

  const hashValueSupportedAlertTypes =
    getHashValueSupportedAlertTypes(subscriptionHashes);

  const { data: initialSaved } = useQuery({
    queryKey: configuredAlertsKeys.detail(alertId),
    queryFn: () =>
      api
        .retreiveSubscription(alertId)
        .then((resp: AxiosResponse<IConfiguredAlertItem>) => resp.data),
    enabled: !!alertId
  });

  const defaultValues = getInitialFormValues(
    shouldUseYaraSubscription,
    shouldUseHashes,
    defaultSubscriptions,
    hashValueSupportedAlertTypes
  );

  const savedValues = useMemo(() => {
    if (!initialSaved) {
      return null;
    }

    if (!shouldUseHashes) {
      return initialSaved;
    }

    const newHashes = subscriptionHashes.map(item => item.sha1);

    const hashValueUpdatedSubscriptions = hashValueSupportedAlertTypes.map(
      hashValueSupportedSub => {
        const existingSubscription = initialSaved.subscriptions.find(
          sub => sub.type === hashValueSupportedSub
        );
        if (existingSubscription) {
          return {
            ...existingSubscription,
            hash_specific: true,
            hash_values: [
              ...(existingSubscription?.hash_values?.filter(
                hash => !newHashes.includes(hash)
              ) ?? []),
              ...newHashes
            ]
          };
        } else {
          return defaultSubscriptions.find(
            sub => sub.type === hashValueSupportedSub
          );
        }
      }
    );

    return {
      ...initialSaved,
      subscriptions: [
        ...initialSaved.subscriptions.filter(
          sub => !hashValueSupportedAlertTypes.includes(sub.type)
        ),
        ...hashValueUpdatedSubscriptions
      ]
    } as IConfiguredAlertData;
  }, [initialSaved, subscriptionHashes]);

  const [values, setValues] = useState<IConfiguredAlertData | null>(null);

  useEffect(() => {
    const initialValues = savedValues || defaultValues;
    setValues(initialValues);
  }, [initialSaved, savedValues]);

  return {
    configuredAlert: values,
    setConfiguredAlert: setValues,
    alertEventCount: initialSaved?.subscriptions?.length || 0
  };
};

export default useConfiguredAlertFormValues;
